import type { FC } from 'react';
import { useMemo } from 'react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Container,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CommonProfileCover,
  HeaderBreadcrumbs,
  Iconify,
  NameAvatar,
  ProfileIcon,
  SvgIconStyle,
  TabsWrapperStyle,
  useTabs,
} from '@pflegenavi/web-components';
import { generatePath, spAppMainPages } from '@pflegenavi/frontend/routing';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useUserProfileApi } from '@pflegenavi/service-provider-frontend/api';
import type { UserProfileDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useGetPaymentProcessor } from '@pflegenavi/frontend/api-nursing-home';

export const ProfilePage: FC = () => {
  const { currentTab, onChangeTab, setCurrentTab } = useTabs('details');
  const location = useLocation();
  const paymentProcessor = useGetPaymentProcessor();

  const tabFromUrl = useMemo(() => {
    if (
      location.pathname.indexOf('/settings') !== -1 &&
      paymentProcessor === 'mangopay'
    ) {
      return 'settings';
    } else if (location.pathname.indexOf('/profile') !== -1) {
      return 'details';
    }
    return 'details';
  }, [location.pathname, paymentProcessor]);

  useEffect(() => {
    setCurrentTab(tabFromUrl);
  }, [setCurrentTab, tabFromUrl]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const PROFILE_TABS = [
    {
      key: 'details',
      value: t('usermenu.profile'),
      icon: (
        <SvgIconStyle
          src={ProfileIcon}
          sx={{
            width: 20,
            mr: 1,
          }}
        />
      ),
      onClick: () => {
        navigate(generatePath(spAppMainPages.PROFILE));
      },
    },
    ...(paymentProcessor === 'mangopay'
      ? [
          {
            key: 'settings',
            value: t('usermenu.settings'),
            icon: <Iconify icon={'ic:settings'} fontSize={20} />,
            onClick: () => {
              navigate(generatePath(spAppMainPages.SETTINGS));
            },
          },
        ]
      : []),
  ];

  return (
    <Box>
      <Container maxWidth={'lg'}>
        <HeaderBreadcrumbs
          heading={t('usermenu.profile')}
          links={[
            {
              name: t('usermenu.profile'),
              href: spAppMainPages.PROFILE,
            },
            { name: t('common.overview') },
          ]}
        />
        <Card
          sx={{
            mb: 3,
            height: 200,
            position: 'relative',
          }}
        >
          <ProfileCover />

          <TabsWrapperStyle>
            <Tabs
              allowScrollButtonsMobile
              scrollButtons="auto"
              value={currentTab}
              onChange={onChangeTab}
            >
              {PROFILE_TABS.map((tab) => (
                <Tab
                  onClick={tab.onClick}
                  disableRipple
                  sx={{ fontSize: 16 }}
                  key={tab.key}
                  value={tab.key}
                  icon={tab.icon}
                  label={tab.value}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </TabsWrapperStyle>
        </Card>
        <Outlet />
      </Container>
    </Box>
  );
};

export const ProfileCover: FC = () => {
  const userProfileApi = useUserProfileApi();
  const [userProfile, setUserProfile] = useState<UserProfileDto | undefined>(
    undefined
  );

  useEffect(() => {
    (async () => {
      const userProfile = await userProfileApi.getUserProfile();
      setUserProfile(userProfile);
    })();
  }, [userProfileApi]);

  if (!userProfile) {
    return <Skeleton width="100%" height="100%" />;
  }

  const name = userProfile?.user
    ? `${userProfile?.user.firstName} ${userProfile?.user.lastName}`
    : '';
  return (
    <CommonProfileCover>
      <StyledNameAvatar fontSize={25} name={name} />
      <NameContainer>
        <Typography variant="h4">{name}</Typography>
      </NameContainer>
    </CommonProfileCover>
  );
};

const NameContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    ml: { md: 2 },
    mt: {
      xs: 1,
      md: 0,
    },
    color: 'text.primary',
    textAlign: {
      xs: 'center',
      md: 'left',
    },
  })
);

const StyledNameAvatar = styled(NameAvatar)(({ theme }) =>
  theme.unstable_sx({
    mx: 'auto',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: 'common.white',
    width: {
      xs: 80,
      md: 100,
    },
    height: {
      xs: 80,
      md: 100,
    },
  })
);
